import { ICombinedMessage, IMessageReferences, StudyType } from "../models/interfaces";

export const HARNESS_SYNTHESIS_KEY = StudyType.Auto;

export const hasResolvedHarnessReferences = (references?: IMessageReferences | null): boolean => {
	if (!references) return false;
	return Object.values(references).some((citationRefs) =>
		Boolean(citationRefs && typeof citationRefs === "object" && Object.keys(citationRefs).length > 0),
	);
};

export const hasResolvedHarnessNamespaceReferences = (
	namespaceReferences?: ICombinedMessage["harnessNamespaceReferences"] | null,
	namespace?: string,
): boolean => {
	if (!namespaceReferences) return false;
	const buckets = namespace ? [namespaceReferences[namespace]] : Object.values(namespaceReferences);
	return buckets.some((refs) => Boolean(refs && typeof refs === "object" && Object.keys(refs).length > 0));
};

export const isHarnessAnswerReferenceLoading = (combinedMessage?: ICombinedMessage): boolean => {
	if (!combinedMessage?.isHarnessTurn) return false;

	// Promoted subagent answer: the Answer tab mirrors a single subagent's stream, so its
	// citation loaders must track that subagent's reference-loading state directly. The
	// Answer-tab heuristic below stops loading the instant the *live* namespace bucket gets
	// its first streamed ref (hasResolvedHarnessNamespaceReferences), but the citation tag
	// renders off the *flat* references which resolve later. Without this delegation the
	// Answer-tab loader is suppressed (and latched off by isProcessComplete) before the
	// references land, so the citation resolves with no loader ever shown. Subagent tabs gate
	// on the flat refs via isHarnessTabReferenceLoading and behave correctly; delegating makes
	// the Answer tab match them. Guarded to non-synthesis keys so there is no recursion back
	// into this function (isHarnessTabReferenceLoading defers to this one only for Auto).
	const promoted = combinedMessage.finalAnswerAgentType;
	if (promoted && promoted !== HARNESS_SYNTHESIS_KEY && isHarnessTabReferenceLoading(combinedMessage, promoted)) {
		return true;
	}

	// The Answer tab stays reference-loading until the answer's references are *resolved* —
	// NOT merely until its text stream completes. For a direct (non-promoted) answer the
	// references arrive on a later `references`/`end` event (which sets referencesResolved[Auto])
	// AFTER isStreamComplete[Auto] is already true. Keying the stop signal off text-stream
	// completion therefore hid the loader during that gap and the citation resolved with no
	// loader shown. referencesResolved is set on every terminal path — end, cancelled, and the
	// aborted-stream `finally` backstop in processHarnessStreamResponse — so it is the
	// authoritative stop signal and cannot leave the loader hanging.
	return Boolean(
		!hasResolvedHarnessReferences(combinedMessage.references?.[HARNESS_SYNTHESIS_KEY]) &&
		!combinedMessage.referencesResolved?.[HARNESS_SYNTHESIS_KEY] &&
		!(
			combinedMessage.finalAnswerAgentType &&
			combinedMessage.referencesResolved?.[combinedMessage.finalAnswerAgentType]
		) &&
		!hasResolvedHarnessNamespaceReferences(
			combinedMessage.harnessNamespaceReferences,
			combinedMessage.finalAnswerAgentType,
		),
	);
};

export const isHarnessTabReferenceLoading = (
	combinedMessage?: ICombinedMessage,
	tabKey: string = HARNESS_SYNTHESIS_KEY,
): boolean => {
	if (!combinedMessage?.isHarnessTurn) return false;
	if (tabKey === HARNESS_SYNTHESIS_KEY) return isHarnessAnswerReferenceLoading(combinedMessage);
	if (hasResolvedHarnessReferences(combinedMessage.references?.[tabKey])) return false;
	if (combinedMessage.referencesResolved?.[tabKey]) return false;

	if (isHarnessReferenceTerminalState(combinedMessage.agentRunStatus?.[tabKey])) return false;

	if (combinedMessage.isStreamComplete && Object.prototype.hasOwnProperty.call(combinedMessage.isStreamComplete, tabKey)) {
		return true;
	}

	return false;
};

const HARNESS_AGENT_PREFIX_BY_TYPE: Record<string, string> = {
	transcripts: "qual",
	qualitative: "qual",
	qual: "qual",
	reports: "reports",
	report: "reports",
	quant: "quant",
	quantitative: "quant",
	notes: "notes",
	note: "notes",
};

const HARNESS_DISPLAY_BASE_BY_PREFIX: Record<string, string> = {
	qual: StudyType.Transcripts,
};

export const getHarnessBaseAgentKey = (agentType?: unknown): string | undefined => {
	if (typeof agentType !== "string") return undefined;
	const normalized = agentType.trim().toLowerCase();
	if (!normalized) return undefined;
	const leafKey = normalized.split(/[.:/]/).filter(Boolean).pop() || normalized;
	const branchMatch = leafKey.match(/^([a-z_]+)_(\d+)$/);
	const baseKey = branchMatch?.[1] || leafKey;
	return HARNESS_AGENT_PREFIX_BY_TYPE[baseKey] || baseKey;
};

export const getHarnessCallIndex = (callIndex?: unknown): number | undefined => {
	if (typeof callIndex === "number" && Number.isFinite(callIndex)) return callIndex;
	if (typeof callIndex === "string" && callIndex.trim() !== "") {
		const parsed = Number(callIndex);
		return Number.isFinite(parsed) ? parsed : undefined;
	}
	return undefined;
};

export const getHarnessAgentDispatchKey = (
	agentType?: unknown,
	callIndex?: unknown,
	invokeRound?: unknown,
): string | undefined => {
	const prefix = getHarnessBaseAgentKey(agentType);
	if (!prefix) return undefined;
	const branchIndex = getHarnessCallIndex(callIndex);
	if (branchIndex === 0) return HARNESS_SYNTHESIS_KEY;
	const uiRound = getHarnessCallIndex(invokeRound);
	if (uiRound !== undefined) return `${prefix}_${uiRound}`;
	if (branchIndex !== undefined) return `${prefix}_${branchIndex}`;

	const normalized = typeof agentType === "string" ? agentType.trim().toLowerCase() : "";
	const leafKey = normalized.split(/[.:/]/).filter(Boolean).pop() || normalized;
	const branchMatch = leafKey.match(/^[a-z_]+_(\d+)$/);
	return branchMatch ? `${prefix}_${branchMatch[1]}` : leafKey;
};

export const getHarnessAgentDisplayBaseKey = (tabKey: string): string => {
	const baseKey = tabKey.replace(/_\d+$/, "");
	return HARNESS_DISPLAY_BASE_BY_PREFIX[baseKey] || baseKey;
};

export type HarnessEventData = {
	[key: string]: unknown;
	Total_time_elapsed?: number;
	agent_type?: string;
	branch_id?: string;
	call_index?: number | string;
	invoke_round?: number | string;
	chat_id?: string;
	chat_name?: string;
	error_kind?: string;
	error_message?: string;
	request_message_id?: string;
	state?: string;
	stream_id?: string;
	timestamp_utc?: string;
};

export type HarnessAgentRunState =
	| "starting"
	| "working"
	| "streaming"
	| "stream_complete"
	| "error"
	| "cancelled"
	| "cancelling";

/** Terminal states for a harness subagent tab — no more events will arrive. */
export const HARNESS_TERMINAL_STATES = new Set<HarnessAgentRunState>([
	"stream_complete",
	"error",
	"cancelled",
]);

export const isHarnessTerminalState = (state?: string | null): boolean =>
	Boolean(state && HARNESS_TERMINAL_STATES.has(state as HarnessAgentRunState));

export const isHarnessReferenceTerminalState = (state?: string | null): boolean =>
	state === "error" || state === "cancelled";

export const isHarnessTabReferenceTerminal = (
	combinedMessage?: ICombinedMessage,
	tabKey: string = HARNESS_SYNTHESIS_KEY,
): boolean => {
	if (!combinedMessage?.isHarnessTurn) return false;
	if (combinedMessage.referencesResolved?.[tabKey]) return true;
	if (
		tabKey === HARNESS_SYNTHESIS_KEY &&
		combinedMessage.finalAnswerAgentType &&
		combinedMessage.referencesResolved?.[combinedMessage.finalAnswerAgentType]
	) {
		return true;
	}
	return isHarnessReferenceTerminalState(combinedMessage.agentRunStatus?.[tabKey]);
};

export type CommonAgentEventState = {
	combinedData: string;
	thinkingData: HarnessEventData[];
	referencesData: IMessageReferences;
};
